import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SectionCard, SectionHeader, SectionLoading, LegendItem } from './ui/Section';
import { useChartStyles, ChartTooltipCard, ChartLoadingOverlay } from './ui/chartStyles';
import { DeiIndexData } from '../hooks/useDeiIndex';
import { useFilterContext } from '../context/FilterContext';
import dayjs from 'dayjs';

const DEI_INFO =
  'The DEI Index uses Brand Love calculations for DEI related content, giving a measure of how well each brand resonates with audiences on DEI topics.';

// Update ChartData to represent a single data point with multiple brand values
interface ChartData {
  date: string;
  fullDate: string;
  displayDate: string;
  index: number;
  [brandKey: string]: string | number; // Allow dynamic brand keys with number values
}

interface DeiIndexProps {
  data: DeiIndexData | null;
  selectedBrands: string[];
  loading?: boolean;
}

// Helper function to determine the best date format based on the date range
const getDateFormat = (dates: string[]): string => {
  if (!dates.length) return 'MMM YYYY'; // Default format
  
  // Parse the dates
  const parsedDates = dates.map(d => dayjs(d));
  
  // Check if all dates are in the same month and year
  const firstDate = parsedDates[0];
  const allSameMonth = parsedDates.every(date => 
    date.month() === firstDate.month() && 
    date.year() === firstDate.year()
  );
  
  // Check if all dates are in the same year
  const allSameYear = parsedDates.every(date => date.year() === firstDate.year());
  
  if (allSameMonth) {
    // If all dates are in the same month, show the day
    return 'MMM D';
  } else if (allSameYear) {
    // If all dates are in the same year, show month and day
    return 'MMM D';
  } else {
    // Default format for dates spanning multiple years
    return 'MMM YYYY';
  }
};

// Helper to calculate optimal tick interval based on data size
const calculateTickInterval = (dataLength: number): number | "preserveStartEnd" => {
  if (dataLength <= 7) return 0; // Show all ticks for a week or less
  if (dataLength <= 14) return 1; // Every other day for two weeks
  if (dataLength <= 31) return 2; // Every third day for a month
  if (dataLength <= 60) return 4; // Every fifth day for two months
  return "preserveStartEnd"; // For longer periods, preserve start and end
};

const DeiIndex: React.FC<DeiIndexProps> = ({ data, selectedBrands, loading = false }) => {
  // Get brand colors from context
  const { brandColors, brandColorsLoading } = useFilterContext();

  // Always call useMemo at the top level, before any conditional returns
  const filteredChartData = useMemo(() => {
    if (!data) return [];

    // Create a chart data point for each date with values for all brands
    const chartData: ChartData[] = data.dei_index_chart.dates.map((date, index) => {
      // Start with date information
      const dataPoint: ChartData = {
        date: dayjs(date).format('MMM D YYYY'),
        fullDate: date,
        displayDate: data.dei_index_chart.displayDates[index],
        index: index
      };
      
      // Add each brand's value to the data point
      data.dei_index_chart.brands.forEach((brand, brandIndex) => {
        // Access the brand's values array safely
        if (data.dei_index_chart.values[brandIndex]) {
          const value = data.dei_index_chart.values[brandIndex][index];
          dataPoint[brand] = value !== null && value !== undefined ? value : 0;
        }
      });
      
      return dataPoint;
    });

    // Date filtering is now done server-side via date_min/date_max params
    // Update indices to ensure they are sequential
    return chartData.map((item, index) => ({
      ...item,
      index: index
    }));
  }, [data]);

  // Generate one tick label per month
  const monthTicks = useMemo(() => {
    if (!filteredChartData.length) return [];
    
    const months: { value: number; label: string }[] = [];
    let currentMonthYear = '';
    
    // Find the positions for each month
    filteredChartData.forEach((point, index) => {
      const monthYear = dayjs(point.fullDate).format('MMM YYYY');
      if (monthYear !== currentMonthYear) {
        currentMonthYear = monthYear;
        months.push({ 
          value: index,
          label: monthYear
        });
      }
    });
    
    return months;
  }, [filteredChartData]);

  // Filter brands to display based on selectedBrands
  const brandsToDisplay = useMemo(() => {
    if (!data) return [];
    
    // If no brands are selected, show all brands (default behavior)
    if (!selectedBrands.length) {
      return data.dei_index_chart.brands;
    }
    
    // Return the intersection of available brands and selected brands
    return data.dei_index_chart.brands.filter(brand => 
      selectedBrands.includes(brand)
    );
  }, [data, selectedBrands]);

  const cs = useChartStyles();
  const header = <SectionHeader title="DEI Index" info={DEI_INFO} />;

  // Show loading skeleton when no data but loading
  if (!data && loading) {
    return (
      <SectionCard>
        {header}
        <SectionLoading label="Loading DEI Index data..." height={380} />
      </SectionCard>
    );
  }

  // If we don't have data yet, show empty state
  if (!data) {
    return (
      <SectionCard>
        {header}
        <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          No DEI Index data for these filters. Try a wider date range or more pillars.
        </Typography>
      </SectionCard>
    );
  }

  // Show loading state while brand colors are loading
  if (brandColorsLoading) {
    return (
      <SectionCard>
        {header}
        <SectionLoading label="Loading brand colors..." height={380} />
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      {header}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1, mb: 2.5 }}>
        {brandsToDisplay.map((brand) => {
          const originalIndex = data.dei_index_chart.brands.indexOf(brand);
          return (
            <LegendItem key={brand} color={brandColors[brand] || `hsl(${originalIndex * 45}, 70%, 50%)`} label={brand} />
          );
        })}
      </Box>

      <Box sx={{ height: 380, position: 'relative' }}>
        {loading && <ChartLoadingOverlay />}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={filteredChartData}
            margin={{
              top: 8,
              right: 16,
              left: 8,
              bottom: 8
            }}
          >
            <CartesianGrid {...cs.grid} />
            <XAxis
              {...cs.xAxis}
              dataKey="index"
              ticks={monthTicks.map(m => m.value)}
              tickFormatter={(value) => {
                const matchingTick = monthTicks.find(t => t.value === value);
                return matchingTick ? matchingTick.label : '';
              }}
            />
            <YAxis
              {...cs.yAxis}
              width={56}
              label={cs.axisLabel('DEI score')}
            />
            <RechartsTooltip
              cursor={cs.lineCursor}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  // Sort payload by value in descending order
                  const sortedPayload = [...payload].sort((a, b) => {
                    const aValue = (a as any).value || 0;
                    const bValue = (b as any).value || 0;
                    return bValue - aValue;
                  });

                  // Get the fullDate from the payload data
                  const dataPoint = payload[0]?.payload;
                  const dateLabel = dataPoint?.fullDate
                    ? dayjs(dataPoint.fullDate).format('MMM D, YYYY')
                    : label;

                  return (
                    <ChartTooltipCard
                      title={dateLabel}
                      rows={sortedPayload.map((entry: any) => ({
                        key: entry.name,
                        color: entry.color,
                        label: entry.name,
                        value: entry.value.toFixed(2),
                      }))}
                    />
                  );
                }
                return null;
              }}
            />
            {brandsToDisplay.map((brand, index) => {
              // Get the brand's original index for consistent color assignment
              const originalIndex = data.dei_index_chart.brands.indexOf(brand);
              return (
              <Line
                key={brand}
                type="monotone"
                dataKey={brand}
                  name={brand}
                  stroke={brandColors[brand] || `hsl(${originalIndex * 45}, 70%, 50%)`}
                dot={false}
                  activeDot={{ r: 3.5, strokeWidth: 0 }}
                  isAnimationActive={false}
                strokeWidth={2}
              />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </SectionCard>
  );
};

export default DeiIndex; 